import React, { useState, useEffect } from 'react';
import { Users, Copy, CheckCircle, Share, Gift } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';

const ReferralCard = () => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await api.get('/referral/stats');
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!referralData?.referral_link) return;
    
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = async () => {
    if (!referralData?.referral_link) return;
    
    const shareData = {
      title: 'Join PayGBot - AI Business Assistant',
      text: 'I\'ve been using PayGBot to grow my online business. Join me!',
      url: referralData.referral_link
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        handleCopyLink();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        handleCopyLink();
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="h-10 bg-white/10 rounded mb-3"></div>
        <div className="h-10 bg-white/10 rounded"></div>
      </div>
    );
  }

  if (!referralData) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-5"
      data-testid="referral-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Gift size={20} className="text-purple-400" weight="fill" />
          <h3 className="text-white font-semibold">Invite Friends</h3>
        </div>
        <div className="flex items-center space-x-1 px-2 py-1 bg-purple-500/20 rounded-full">
          <Users size={14} className="text-purple-400" />
          <span className="text-sm text-purple-400 font-bold">{referralData.referral_count}</span>
        </div>
      </div>

      {/* Referral Code */}
      <div className="mb-4">
        <label className="block text-xs text-slate-400 mb-1">Your Referral Code</label>
        <div className="flex items-center space-x-2">
          <div className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg">
            <span className="text-white font-mono font-bold tracking-wider">
              {referralData.referral_code}
            </span>
          </div>
          <button
            onClick={handleCopyLink}
            className={`p-2 rounded-lg transition-all ${
              copied
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
            data-testid="copy-referral-link"
          >
            {copied ? <CheckCircle size={20} weight="fill" /> : <Copy size={20} />}
          </button>
        </div>
      </div>

      {/* Share Button */}
      <button
        onClick={handleShare}
        className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2"
        data-testid="share-referral"
      >
        <Share size={18} weight="fill" />
        <span>Share Invite Link</span>
      </button>

      {/* Recent Referrals */}
      {referralData.referred_users && referralData.referred_users.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <label className="block text-xs text-slate-400 mb-2">Recent Invites</label>
          <div className="space-y-2">
            {referralData.referred_users.slice(0, 3).map((user, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                  <span className="text-xs text-white font-bold">
                    {user.name?.charAt(0).toUpperCase() || '?'}
                  </span>
                </div>
                <span className="text-sm text-slate-300">{user.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ReferralCard;
